import { useState } from 'react';
import { Link, Navigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { Wordmark } from '@/components/Wordmark';
import { useAuth } from '@/features/auth/AuthProvider';
import { GoogleButton } from './GoogleButton';

/**
 * Self-serve sign-up (cloud/pi only — routed from router.cloud). Creates a
 * Firebase account (email/password or Google) and a server-side pending
 * profile; the app then routes the user to the pending-approval holding
 * screen until an admin approves.
 */
export function SignupPage() {
  const { state, signup } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (state.status === 'authenticated') return <Navigate to="/" replace />;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signup(email, password, name);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'sign-up failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto mt-4 max-w-md rounded-lg border border-[color:var(--color-border)] bg-card p-4 shadow-sm sm:mt-12 sm:p-6">
      <div className="mb-4 flex items-center gap-3">
        <Wordmark className="h-8 w-auto text-[color:var(--color-primary)] sm:h-10" />
        <div>
          <h1 className="text-lg font-bold leading-tight sm:text-xl">Create your account</h1>
          <p className="text-xs text-[color:var(--color-muted-foreground)]">
            New accounts are activated after admin approval
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <GoogleButton mode="signup" onError={setError} />
      </div>

      <div className="my-4 flex items-center gap-3 text-xs text-[color:var(--color-muted-foreground)]">
        <span className="h-px flex-1 bg-[color:var(--color-border)]" />
        or
        <span className="h-px flex-1 bg-[color:var(--color-border)]" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <label className="block">
          <span className="text-sm font-medium">Name</span>
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="signup-name"
            className="mt-1 w-full rounded border border-[color:var(--color-muted)] px-2 py-1 text-sm"
            placeholder="Your name"
            autoComplete="name"
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium">Email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="signup-email"
            className="mt-1 w-full rounded border border-[color:var(--color-muted)] px-2 py-1 text-sm font-mono"
            placeholder="you@example.com"
            autoComplete="email"
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium">Password</span>
          <input
            type="password"
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="signup-password"
            className="mt-1 w-full rounded border border-[color:var(--color-muted)] px-2 py-1 text-sm font-mono"
            autoComplete="new-password"
          />
        </label>
        {error ? (
          <p className="text-sm text-[color:var(--color-danger)]" data-testid="signup-error">
            {error}
          </p>
        ) : null}
        <Button
          type="submit"
          disabled={submitting || !email || !password || !name}
          data-testid="signup-submit"
          className="w-full"
        >
          {submitting ? 'Creating account…' : 'Create account'}
        </Button>
      </form>

      <p className="mt-4 text-center text-xs text-[color:var(--color-muted-foreground)]">
        Already have an account?{' '}
        <Link
          to="/login"
          className="font-semibold text-[color:var(--color-primary)] hover:underline"
        >
          Log in
        </Link>
      </p>
    </div>
  );
}
